/**
 * One budget option's lines as the seller sees them: a group per audience
 * with its subtotal, the added-value block and TOTAL. Internal detail stays
 * in: AdOps placement names (copyable on the plan page), base + upcharges
 * behind each net CPM, the card price behind a typed rate, and where the rate
 * came from. The client Sheet / PDF hide all of that.
 */

import React, { useMemo } from 'react';
import { IconButton, CopyIcon, tableStyles } from '../../ui';
import type { MediaPlanLine, MediaPlanRequest, MediaPlanTier } from './types';
import { RATE_BASIS_LABELS } from './vocab';
import { fmtInt, fmtPct, fmtUsd, monoNum, mutedText } from './formStyles';

interface Props {
  tier: MediaPlanTier;
  request: MediaPlanRequest;
  /** Show a copy button beside each placement name (the plan page). */
  onCopyPlacement?: (placementName: string) => void;
}

export interface LineGroup {
  key: string;
  label: string;
  description?: string;
  budgetUsd: number;
  budgetPct: number;
  lines: MediaPlanLine[];
  paidImpressions: number;
  paidCostUsd: number;
}

export function groupsFor(tier: MediaPlanTier, request: MediaPlanRequest): LineGroup[] {
  if (tier.segments && tier.segments.length > 0) {
    return tier.segments.map((s) => {
      const lines = tier.lines.filter((l) => l.segmentKey === s.key && !l.isAddedValue);
      return { key: s.key, label: s.label, description: s.description, budgetUsd: s.budgetUsd, budgetPct: s.budgetPct, lines, paidImpressions: s.totals.paidImpressions, paidCostUsd: s.totals.paidCostUsd };
    });
  }
  const demo = (request.targeting?.demo || []).filter(Boolean);
  return [
    {
      key: 'all',
      label: demo.length ? demo.join(' ') : 'General Market',
      budgetUsd: tier.budgetUsd,
      budgetPct: 100,
      lines: tier.lines.filter((l) => !l.isAddedValue),
      paidImpressions: tier.totals.paidImpressions,
      paidCostUsd: tier.totals.paidCostUsd,
    },
  ];
}

export const mediaTypeOf = (line: MediaPlanLine): string => {
  if (line.isAddedValue) return 'Added Value';
  if (line.unitKey === 'sov-takeover') return 'Channel Takeover';
  return line.platform === 'ctv' ? 'Connected TV (CTV)' : 'Online Video (OLV)';
};

const RateCell: React.FC<{ line: MediaPlanLine }> = ({ line }) => {
  if (line.isAddedValue) return <span style={mutedText}>—</span>;
  if (line.rateType === 'flat') return <span>{fmtUsd(line.flatUsd || line.costUsd, 0)} flat</span>;
  const parts: string[] = [];
  if (line.rateBasis) parts.push(RATE_BASIS_LABELS[line.rateBasis].toLowerCase());
  if (line.baseCpmSource === 'override') parts.push(`card ${typeof line.cardCpm === 'number' && line.cardCpm > 0 ? fmtUsd(line.cardCpm) : 'unpriced'}`);
  if (line.upcharges.length) parts.push(`base ${fmtUsd(line.baseCpm)} + ${line.upcharges.map((u) => fmtUsd(u.cpmAdd)).join(' + ')}`);
  return (
    <span>
      <strong style={monoNum}>{fmtUsd(line.netCpm)}</strong>
      {parts.length > 0 && <div style={{ ...mutedText, fontSize: 11 }}>{parts.join(' · ')}</div>}
    </span>
  );
};

const PlanLinesTable: React.FC<Props> = ({ tier, request, onCopyPlacement }) => {
  const groups = useMemo(() => groupsFor(tier, request), [tier, request]);
  const av = tier.lines.filter((l) => l.isAddedValue);
  const t = tier.totals;
  return (
    <>
      <div style={tableStyles.wrapScroll}>
        <table style={tableStyles.table}>
          <thead>
            <tr>
              <th style={tableStyles.th}>Media type</th>
              <th style={tableStyles.th}>Placement</th>
              <th style={tableStyles.thNum}>Net CPM</th>
              <th style={tableStyles.thNum}>Impressions</th>
              <th style={tableStyles.thNum}>Investment</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <React.Fragment key={g.key}>
                <tr style={{ background: 'var(--bg-surface-2)' }}>
                  <td style={{ ...tableStyles.td, fontWeight: 700 }} colSpan={2}>
                    {g.label}
                    {groups.length > 1 && <span style={{ ...mutedText, marginLeft: 8 }}>{fmtPct(g.budgetPct)} · {fmtUsd(g.budgetUsd, 0)}</span>}
                    {g.description && <div style={{ ...mutedText, fontWeight: 400 }}>{g.description}</div>}
                  </td>
                  <td style={tableStyles.tdNum} />
                  <td style={{ ...tableStyles.tdNum, fontWeight: 700 }}>{fmtInt(g.paidImpressions)}</td>
                  <td style={{ ...tableStyles.tdNum, fontWeight: 700 }}>{fmtUsd(g.paidCostUsd)}</td>
                </tr>
                {g.lines.map((l) => (
                  <tr key={l.id}>
                    <td style={tableStyles.td}>{mediaTypeOf(l)}</td>
                    <td style={tableStyles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span>{l.placementName}</span>
                        {onCopyPlacement && (
                          <IconButton size="sm" variant="ghost" label={`Copy placement name ${l.placementName}`} onClick={() => onCopyPlacement(l.placementName)}>
                            <CopyIcon />
                          </IconButton>
                        )}
                      </div>
                      <div style={{ ...mutedText, fontSize: 11 }}>{l.geography}</div>
                    </td>
                    <td style={tableStyles.tdNum}>
                      <RateCell line={l} />
                    </td>
                    <td style={tableStyles.tdNum}>{l.rateType === 'cpm' ? fmtInt(l.impressions) : 'TBD'}</td>
                    <td style={tableStyles.tdNum}>{fmtUsd(l.costUsd)}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {av.length > 0 && (
              <>
                <tr style={{ background: 'var(--bg-surface-2)' }}>
                  <td style={{ ...tableStyles.td, fontWeight: 700 }} colSpan={3}>
                    Added value <span style={{ ...mutedText, marginLeft: 8 }}>included at no charge</span>
                  </td>
                  <td style={{ ...tableStyles.tdNum, fontWeight: 700 }}>{fmtInt(t.addedValueImpressions)}</td>
                  <td style={{ ...tableStyles.tdNum, fontWeight: 700 }}>—</td>
                </tr>
                {av.map((l) => (
                  <tr key={l.id}>
                    <td style={tableStyles.td}>Added Value</td>
                    <td style={tableStyles.td}>
                      {l.placementName.replace(/^Added Value — /, '')}
                      {l.segmentLabel && <span style={{ ...mutedText, marginLeft: 6 }}>· {l.segmentLabel}</span>}
                      {l.notes && <div style={{ ...mutedText, fontSize: 11 }}>{l.notes}</div>}
                    </td>
                    <td style={tableStyles.tdNum}>
                      <span style={mutedText}>—</span>
                    </td>
                    <td style={tableStyles.tdNum}>{l.impressions > 0 ? fmtInt(l.impressions) : '—'}</td>
                    <td style={tableStyles.tdNum}>
                      {typeof l.addedValueValueUsd === 'number' && l.addedValueValueUsd > 0 ? <span style={mutedText}>{fmtUsd(l.addedValueValueUsd, 0)} value</span> : <span style={mutedText}>Included</span>}
                    </td>
                  </tr>
                ))}
              </>
            )}
            <tr style={{ background: 'var(--bg-surface-3)' }}>
              <td style={{ ...tableStyles.td, fontWeight: 800 }} colSpan={3}>
                TOTAL
              </td>
              <td style={{ ...tableStyles.tdNum, fontWeight: 800 }}>{fmtInt(t.totalImpressions)}</td>
              <td style={{ ...tableStyles.tdNum, fontWeight: 800 }}>{fmtUsd(t.paidCostUsd)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {t.remainingBudgetUsd >= 1 && <div style={mutedText}>{fmtUsd(t.remainingBudgetUsd)} of the budget could not be allocated to whole impressions.</div>}
    </>
  );
};

export default PlanLinesTable;
